import { Color } from "three";

export type WallPalette = "cork" | "olive" | "slate";

interface PaletteSpec {
  base: number;
  accent: number;
}

export const WALL_PALETTES: Record<WallPalette, PaletteSpec> = {
  cork: { base: 0xb3906b, accent: 0xc06a45 },
  olive: { base: 0x9aa06e, accent: 0xc0a045 },
  slate: { base: 0x8a93a3, accent: 0x5f7fa8 },
};

export type WallPattern = "none" | "noise" | "voronoi";

export interface WallParams {
  pattern: WallPattern;
  cells: number;
  octaves: number;
  palette: WallPalette;
  accentRatio: number;
  edgeWidth: number;
  edgeDepth: number;
  detail: number;
}

export const WALL_TEX_W = 1024;
export const WALL_TEX_H = 1024;

export const WALL_DEFAULT_PARAMS: WallParams = {
  pattern: "voronoi",
  cells: 48,
  octaves: 4,
  palette: "cork",
  accentRatio: 0.22,
  edgeWidth: 0.06,
  edgeDepth: 0.5,
  detail: 0.15,
};

export const WALL_DEFAULT_TILE = 1;
export const WALL_DEFAULT_NORMAL_SCALE = 0.75;
export const WALL_NOISE_NORMAL_SCALE = 0.35;
export const WALL_NOISE_ACCENT_RATIO = 0.8;
export const WALL_DEFAULT_Z = -1.2;

export interface WallResolution {
  key: string;
  w: number;
  h: number;
}

export const WALL_RESOLUTIONS: WallResolution[] = [
  { key: "full", w: WALL_TEX_W, h: WALL_TEX_H },
  { key: "half", w: WALL_TEX_W / 2, h: WALL_TEX_H / 2 },
  { key: "quarter", w: WALL_TEX_W / 4, h: WALL_TEX_H / 4 },
  { key: "eighth", w: WALL_TEX_W / 8, h: WALL_TEX_H / 8 },
];

export const WALL_DEFAULT_RESOLUTION = "full";

export type WallPreset =
  | "none"
  | "noise-cork"
  | "noise-olive"
  | "noise-slate"
  | "voronoi-cork"
  | "voronoi-olive"
  | "voronoi-slate";

export const WALL_PRESETS: WallPreset[] = [
  "none",
  "noise-cork",
  "noise-olive",
  "noise-slate",
  "voronoi-cork",
  "voronoi-olive",
  "voronoi-slate",
];

export const WALL_DEFAULT_PRESET: WallPreset = "voronoi-cork";

const WALL_PRESET_DEFS: Record<Exclude<WallPreset, "none">, { pattern: WallPattern; palette: WallPalette }> = {
  "noise-cork": { pattern: "noise", palette: "cork" },
  "noise-olive": { pattern: "noise", palette: "olive" },
  "noise-slate": { pattern: "noise", palette: "slate" },
  "voronoi-cork": { pattern: "voronoi", palette: "cork" },
  "voronoi-olive": { pattern: "voronoi", palette: "olive" },
  "voronoi-slate": { pattern: "voronoi", palette: "slate" },
};

export function wallPresetParams(preset: WallPreset): WallParams {
  if (preset === "none") {
    return { ...WALL_DEFAULT_PARAMS, pattern: "none" };
  }
  const def = WALL_PRESET_DEFS[preset];
  return {
    ...WALL_DEFAULT_PARAMS,
    pattern: def.pattern,
    palette: def.palette,
    accentRatio: def.pattern === "noise" ? WALL_NOISE_ACCENT_RATIO : WALL_DEFAULT_PARAMS.accentRatio,
  };
}

export function wallParamsToPreset(params: WallParams): WallPreset {
  if (params.pattern === "none") {
    return "none";
  }
  return `${params.pattern}-${params.palette}` as WallPreset;
}

export function wallPresetNormalScale(preset: WallPreset): number {
  if (preset !== "none" && WALL_PRESET_DEFS[preset].pattern === "noise") {
    return WALL_NOISE_NORMAL_SCALE;
  }
  return WALL_DEFAULT_NORMAL_SCALE;
}

export interface WallMapData {
  normal: Uint8Array;
  color: Uint8Array | null;
  sizeX: number;
  sizeY: number;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Tileable multi-octave value noise height field. Lattice indices wrap at each
 * octave's cell count so RepeatWrapping is seamless.
 */
function fbmHeight(sizeX: number, sizeY: number, cells: number, octaves: number): Float32Array {
  const aspect = sizeX / sizeY;
  const height = new Float32Array(sizeX * sizeY);
  for (let o = 0; o < octaves; o++) {
    const cy = cells << o;
    const cx = cy * aspect;
    const amp = 1 / (1 << o);
    const grid = new Float32Array(cx * cy);
    for (let i = 0; i < grid.length; i++) {
      grid[i] = Math.random();
    }
    for (let y = 0; y < sizeY; y++) {
      for (let x = 0; x < sizeX; x++) {
        const gx = (x / sizeX) * cx;
        const gy = (y / sizeY) * cy;
        const x0 = Math.floor(gx);
        const y0 = Math.floor(gy);
        const x1 = (x0 + 1) % cx;
        const y1 = (y0 + 1) % cy;
        const tx = gx - x0;
        const ty = gy - y0;
        const fx = tx * tx * (3 - 2 * tx);
        const fy = ty * ty * (3 - 2 * ty);
        const top = lerp(grid[y0 * cx + x0], grid[y0 * cx + x1], fx);
        const bottom = lerp(grid[y1 * cx + x0], grid[y1 * cx + x1], fx);
        height[y * sizeX + x] += lerp(top, bottom, fy) * amp;
      }
    }
  }
  return height;
}

interface WorleyField {
  height: Float32Array;
  cellIndex: Int32Array;
  cellCount: number;
}

function worleyField(sizeX: number, sizeY: number, cells: number, edgeWidth: number): WorleyField {
  const aspect = sizeX / sizeY;
  const cx = cells * aspect;
  const cy = cells;
  const px = new Float32Array(cx * cy);
  const py = new Float32Array(cx * cy);
  for (let i = 0; i < px.length; i++) {
    px[i] = Math.random();
    py[i] = Math.random();
  }
  const height = new Float32Array(sizeX * sizeY);
  const cellIndex = new Int32Array(sizeX * sizeY);
  const sx = new Float32Array(9);
  const sy = new Float32Array(9);
  const si = new Int32Array(9);
  for (let y = 0; y < sizeY; y++) {
    for (let x = 0; x < sizeX; x++) {
      const gx = (x / sizeX) * cx;
      const gy = (y / sizeY) * cy;
      const x0 = Math.floor(gx);
      const y0 = Math.floor(gy);
      let f1 = Infinity;
      let best = 0;
      let n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = (x0 + dx + cx) % cx;
          const ny = (y0 + dy + cy) % cy;
          const i = ny * cx + nx;
          const ox = x0 + dx + px[i] - gx;
          const oy = y0 + dy + py[i] - gy;
          sx[n] = ox;
          sy[n] = oy;
          si[n] = i;
          const d = ox * ox + oy * oy;
          if (d < f1) {
            f1 = d;
            best = n;
          }
          n++;
        }
      }
      const ax = sx[best];
      const ay = sy[best];
      let border = Infinity;
      for (let j = 0; j < 9; j++) {
        if (j === best) {
          continue;
        }
        const bx = sx[j] - ax;
        const by = sy[j] - ay;
        const len = Math.hypot(bx, by);
        if (len < 1e-6) {
          continue;
        }
        const d = ((ax + sx[j]) * bx + (ay + sy[j]) * by) / (2 * len);
        if (d < border) {
          border = d;
        }
      }
      const t = Math.min(border / edgeWidth, 1);
      const p = y * sizeX + x;
      height[p] = t * t * (3 - 2 * t);
      cellIndex[p] = si[best];
    }
  }
  return { height, cellIndex, cellCount: cx * cy };
}

function cellColorData(
  field: WorleyField,
  sizeX: number,
  sizeY: number,
  spec: PaletteSpec,
  accentRatio: number,
): Uint8Array {
  const palette = new Float32Array(field.cellCount * 3);
  const base = new Color(spec.base);
  const accent = new Color(spec.accent);
  const c = new Color();
  for (let i = 0; i < field.cellCount; i++) {
    c.copy(base).multiplyScalar(0.85 + Math.random() * 0.25);
    if (Math.random() < accentRatio) {
      c.lerp(accent, 0.3 + Math.random() * 0.4);
    }
    c.convertLinearToSRGB();
    palette[i * 3] = Math.min(c.r, 1);
    palette[i * 3 + 1] = Math.min(c.g, 1);
    palette[i * 3 + 2] = Math.min(c.b, 1);
  }
  const data = new Uint8Array(sizeX * sizeY * 4);
  for (let p = 0; p < sizeX * sizeY; p++) {
    const cell = field.cellIndex[p] * 3;
    data[p * 4] = palette[cell] * 255;
    data[p * 4 + 1] = palette[cell + 1] * 255;
    data[p * 4 + 2] = palette[cell + 2] * 255;
    data[p * 4 + 3] = 255;
  }
  return data;
}

/**
 * Tints a normalized height field from base (peaks) toward accent (valleys),
 * so the noise pattern's low/shadowed regions read in the accent color. The
 * accent reaches its full lerp amount of accentRatio at the deepest valley.
 */
function heightColorData(
  height: Float32Array,
  sizeX: number,
  sizeY: number,
  spec: PaletteSpec,
  accentRatio: number,
): Uint8Array {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < height.length; i++) {
    if (height[i] < min) {
      min = height[i];
    }
    if (height[i] > max) {
      max = height[i];
    }
  }
  const range = max - min || 1;
  const base = new Color(spec.base);
  const accent = new Color(spec.accent);
  const c = new Color();
  const data = new Uint8Array(sizeX * sizeY * 4);
  for (let p = 0; p < height.length; p++) {
    const valley = 1 - (height[p] - min) / range;
    c.copy(base).lerp(accent, accentRatio * valley);
    c.convertLinearToSRGB();
    data[p * 4] = Math.min(c.r, 1) * 255;
    data[p * 4 + 1] = Math.min(c.g, 1) * 255;
    data[p * 4 + 2] = Math.min(c.b, 1) * 255;
    data[p * 4 + 3] = 255;
  }
  return data;
}

function heightToNormalData(height: Float32Array, sizeX: number, sizeY: number, strength: number): Uint8Array {
  const data = new Uint8Array(sizeX * sizeY * 4);
  for (let y = 0; y < sizeY; y++) {
    for (let x = 0; x < sizeX; x++) {
      const xm = (x - 1 + sizeX) % sizeX;
      const xp = (x + 1) % sizeX;
      const ym = (y - 1 + sizeY) % sizeY;
      const yp = (y + 1) % sizeY;
      const dx = (height[y * sizeX + xp] - height[y * sizeX + xm]) * strength;
      const dy = (height[yp * sizeX + x] - height[ym * sizeX + x]) * strength;
      const inv = 1 / Math.hypot(dx, dy, 1);
      const i = (y * sizeX + x) * 4;
      data[i] = (-dx * inv * 0.5 + 0.5) * 255;
      data[i + 1] = (-dy * inv * 0.5 + 0.5) * 255;
      data[i + 2] = (inv * 0.5 + 0.5) * 255;
      data[i + 3] = 255;
    }
  }
  return data;
}

/**
 * Pure CPU bake of the wall maps as raw RGBA byte arrays. Lives apart from
 * THREE.DataTexture wrapping so it can run inside a Web Worker (only THREE's
 * Color math is pulled in, never the WebGL texture types).
 */
export function computeWallMapData(params: WallParams, sizeX: number, sizeY: number): WallMapData {
  const spec = WALL_PALETTES[params.palette];
  if (params.pattern === "voronoi") {
    const field = worleyField(sizeX, sizeY, params.cells, params.edgeWidth);
    const color = cellColorData(field, sizeX, sizeY, spec, params.accentRatio);
    const detail = fbmHeight(sizeX, sizeY, params.cells * 2, params.octaves);
    for (let i = 0; i < field.height.length; i++) {
      field.height[i] = field.height[i] * (1 - params.detail) + detail[i] * params.detail;
    }
    return { normal: heightToNormalData(field.height, sizeX, sizeY, params.edgeDepth), color, sizeX, sizeY };
  }
  const height = fbmHeight(sizeX, sizeY, params.cells, params.octaves);
  return {
    normal: heightToNormalData(height, sizeX, sizeY, 6),
    color: heightColorData(height, sizeX, sizeY, spec, params.accentRatio),
    sizeX,
    sizeY,
  };
}
